import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo
} from "react";
import { socketManager } from "../socket/socketManager";
import { useBridgeStore } from "../store/apis/useBridgeStore";

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider = React.memo(({ children, user }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionHealth, setConnectionHealth] = useState(null);
  const userRef = useRef(user);
  const cleanup = useRef(null);
  const healthCheckInterval = useRef(null);
  const hasConnectedRef = useRef(false);

  // Update user ref when user changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Start health monitoring
  const startHealthMonitoring = useCallback(() => {
    if (healthCheckInterval.current) {
      clearInterval(healthCheckInterval.current);
    }

    healthCheckInterval.current = setInterval(() => {
      const health = socketManager.getConnectionHealth();
      setConnectionHealth(health);
      
      // Auto-reconnect if connection seems dead (hidden tabs throttle the heartbeat)
      if (
        document.visibilityState === "visible" &&
        health.connected &&
        health.timeSinceLastPong > 120000 // 2 minutes
      ) {
        console.log("Connection appears dead, forcing reconnect");
        socketManager.forceReconnect();
      }
      
      // If websocket keeps failing, try polling
      if (!health.connected && health.reconnectAttempts > 3) {
        console.log("Multiple reconnection failures, trying transport downgrade");
        socketManager.forceTransportDowngrade();
      }
    }, 15000); // Check every 15 seconds
  }, []);

  const stopHealthMonitoring = useCallback(() => {
    if (healthCheckInterval.current) {
      clearInterval(healthCheckInterval.current);
      healthCheckInterval.current = null;
    }
  }, []);

  const handleConnection = useCallback(() => {
    if (!user?._id) {
      socketManager.disconnect();
      setIsConnected(false);
      stopHealthMonitoring();
      return;
    }

    try {
      const socket = socketManager.connect({
        query: { userId: user._id },
      });

      if (socket) {
        // Connection event handlers
        const onConnect = () => {
          console.log("Socket connected successfully");
          setIsConnected(true);
          setConnectionError(null);
          startHealthMonitoring();
          // Events sent while disconnected are lost; re-read the sidebar and the open chat
          if (hasConnectedRef.current) {
            const { triggerSidebarRefresh, triggerSocketReconnected } = useBridgeStore.getState();
            triggerSidebarRefresh(Date.now());
            triggerSocketReconnected();
          }
          hasConnectedRef.current = true;
        };

        const onDisconnect = (reason) => {
          console.log("Socket disconnected:", reason);
          setIsConnected(false);
          stopHealthMonitoring();
          
          if (reason !== "io client disconnect") {
            setConnectionError(`Disconnected: ${reason}`);
          }
        };

        const onConnectError = (error) => {
          console.error("Socket connection error:", error);
          setConnectionError(error.message || "Connection failed");
          setIsConnected(false);
          stopHealthMonitoring();
        };

        const onReconnect = () => {
          console.log("Socket reconnected");
          setIsConnected(true);
          setConnectionError(null);
          startHealthMonitoring();
        };

        // Attach handlers
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onConnectError);
        socket.on("reconnect", onReconnect);

        // Store cleanup function
        cleanup.current = () => {
          socket.off("connect", onConnect);
          socket.off("disconnect", onDisconnect);
          socket.off("connect_error", onConnectError);
          socket.off("reconnect", onReconnect);
          stopHealthMonitoring();
        };

        // Set initial connection state
        setIsConnected(socket.connected);
        if (socket.connected) {
          startHealthMonitoring();
        }
      }
    } catch (error) {
      console.error("Socket connection error:", error);
      setConnectionError(error.message);
      stopHealthMonitoring();
    }
  }, [user?._id, startHealthMonitoring, stopHealthMonitoring]);

  // Handle page focus/blur for better idle management
  // Uses socketManager.forceReconnect() instead of handleConnection() to avoid
  // creating a new socket instance and duplicating React-level event handlers.
  useEffect(() => {
    const handleFocus = () => {
      if (userRef.current?._id && !socketManager.isConnected()) {
        console.log("Reconnecting on focus");
        socketManager.forceReconnect();
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", () => {});

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", () => {});
    };
  }, []);

  // Handle visibility change for mobile browsers
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("Tab visible - verifying connection");
        if (userRef.current?._id && !socketManager.isConnected()) {
          console.log("Tab visible, attempting reconnection...");
          socketManager.forceReconnect();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser came online - checking connection");
      if (userRef.current?._id && !socketManager.isConnected()) {
        setTimeout(() => socketManager.forceReconnect(), 1000);
      }
    };

    const handleOffline = () => {
      console.log("Browser went offline");
      setConnectionError("No internet connection");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Main connection effect
  useEffect(() => {
    handleConnection();

    return () => {
      cleanup.current?.();
      stopHealthMonitoring();
      if (!user?._id) {
        socketManager.disconnect();
      }
    };
  }, [handleConnection, stopHealthMonitoring, user?._id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHealthMonitoring();
    };
  }, [stopHealthMonitoring]);

  const contextValue = useMemo(
    () => ({
      socket: socketManager,
      isConnected,
      connectionError,
      connectionHealth,
      userId: user?._id || null,
      forceReconnect: () => {
        console.log("Manual reconnection triggered");
        socketManager.forceReconnect();
      },
      forceTransportDowngrade: () => {
        console.log("Manual transport downgrade triggered");
        socketManager.forceTransportDowngrade();
      },
      tryTransportUpgrade: () => {
        console.log("Manual transport upgrade triggered");
        socketManager.tryTransportUpgrade();
      },
    }),
    [isConnected, connectionError, connectionHealth, user?._id]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
});